import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase/config';

export type OTPMethod = 'sms' | 'whatsapp';

// Client-safe settings: no secret field. The Authentica credential never
// leaves the server — see the `authenticaConfig` callable in
// functions/index.js, which reads settings/authenticaApi with the Admin SDK
// and returns only these two fields. The key itself is the server secret
// AUTHENTICA_API_KEY (namat#130); no client reads or writes it.
interface AuthenticaSettings {
  configured: boolean;
  otpMethod: OTPMethod;
}

let _cachedSettings: AuthenticaSettings | null = null;
let _cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const LS_KEY = 'namat.authenticaSettings.v2';
const LS_TTL = 24 * 60 * 60 * 1000; // 1 day

function readLocalCache(): AuthenticaSettings | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; v: AuthenticaSettings };
    if (!parsed.v || typeof parsed.v.configured !== 'boolean') return null;
    if (Date.now() - parsed.ts > LS_TTL) return null;
    return parsed.v;
  } catch {
    return null;
  }
}

function writeLocalCache(v: AuthenticaSettings) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ ts: Date.now(), v }));
  } catch {
    /* ignore quota / SSR */
  }
}

export function clearAuthenticaCache() {
  _cachedSettings = null;
  _cacheTimestamp = 0;
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }
}

export async function getAuthenticaSettings(): Promise<AuthenticaSettings | null> {
  const now = Date.now();
  if (_cachedSettings && (now - _cacheTimestamp) < CACHE_TTL) {
    return _cachedSettings;
  }

  // Retry on transient network errors (common on cold-load of a public page
  // before the connection has settled).
  for (let attempt = 0; attempt < 5; attempt++) {
    try {
      const call = httpsCallable<Record<string, never>, { configured: boolean; otpMethod: OTPMethod | null }>(
        functions,
        'authenticaConfig'
      );
      const { data } = await call({});
      if (data.configured) {
        _cachedSettings = {
          configured: true,
          otpMethod: data.otpMethod || 'sms',
        };
        _cacheTimestamp = Date.now();
        writeLocalCache(_cachedSettings);
        return _cachedSettings;
      }
      return null;
    } catch (e) {
      if (attempt === 4) {
        console.warn('Could not fetch Authentica config from authenticaConfig callable:', e);
        // Last resort: serve from localStorage so we don't fall through to the
        // CORS-blocked Evolution browser path while functions are unreachable.
        const local = readLocalCache();
        if (local) {
          _cachedSettings = local;
          _cacheTimestamp = Date.now();
          return local;
        }
        return null;
      }
      await new Promise((r) => setTimeout(r, 300 * Math.pow(2, attempt)));
    }
  }

  return null;
}

export async function getOTPMethod(): Promise<OTPMethod> {
  const settings = await getAuthenticaSettings();
  return settings?.otpMethod || 'whatsapp';
}

// sendAuthenticaOTP / verifyAuthenticaOTP were removed 2026-08-14: they sent
// the Authentica API key from the browser (`X-Authorization`) straight to
// api.authentica.sa. OTP send/verify now go through the server-side
// sendOtpCode/verifyOtpCode callables — see
// src/lib/firebase/auth/whatsapp-phone-auth.ts and
// src/lib/services/otp-verification.ts.
//
// getAuthenticaSettings() was itself rewritten the same day: it used to read
// settings/authenticaApi (including the secret credential) directly from
// Firestore, which shipped it to every browser session (AuthContext
// pre-warms this on mount, use-otp-method.ts calls getOTPMethod()). It now
// calls the `authenticaConfig` callable, which never returns the credential.
// Since namat#130 the credential is the server secret AUTHENTICA_API_KEY and
// the admin config form (UserSettingsDialog) no longer reads or writes it
// either; it shows only whether it is configured (`messagingProviderStatus`).
